import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, EyeOff, Inbox, Loader2, Lock, RotateCw, type LucideIcon } from 'lucide-react';
import { errorDetail, normalizeKind, stateKindForError, type StateKind, type StateKindInput } from './stateBlockModel';
import Icon from './Icon';

export type { StateKind, StateKindInput } from './stateBlockModel';
export { errorStatus, stateKindForError, errorDetail, safeMetric } from './stateBlockModel';

export type StateBlockSize = 'page' | 'panel' | 'inline';

export interface StateBlockProps {
  /** loading · empty · error · unavailable (404 / feature off) · denied (403; alias `forbidden`). */
  kind: StateKindInput;
  /** page = a whole list or screen; panel = a section inside a card; inline = one quiet row. */
  size?: StateBlockSize;
  /** Main line. Each kind has a default (states.*). */
  title?: ReactNode;
  /** One supporting line in text-2. Each kind except `empty` has a default; `null` hides it. */
  description?: ReactNode;
  /** Glyph; `null` hides it. Each kind has a default. */
  icon?: LucideIcon | null;
  /** The one next action (empty states: "ניקוי סינון", "הוספת …"). */
  action?: ReactNode;
  /** Retry for `error` (usually a react-query `refetch`): always "ניסיון חוזר". */
  onRetry?: () => void;
  /** The retry is in flight (spinner inside the button, double press blocked). */
  retrying?: boolean;
  /**
   * The rejection itself. With `kind="error"` a 403 becomes `denied` and a 404
   * `unavailable` (no retry: it would not help), and the server message goes
   * under "פרטים טכניים", never in the main line.
   */
  error?: unknown;
  /** Smaller glyph and padding, for dashboard widgets and narrow panels. */
  compact?: boolean;
  /** Skeleton rows for `loading` (page / panel). */
  rows?: number;
  className?: string;
}

const DEFAULT_ICON: Record<StateKind, LucideIcon | null> = {
  loading: null,
  empty: Inbox,
  error: AlertCircle,
  unavailable: EyeOff,
  denied: Lock,
};

/**
 * The one empty / error / loading / unavailable / denied state
 * (DESIGN_SYSTEM.md §14, PRD §4.13), in the visual language of the Home
 * states: a glyph without a disc (text-disabled; danger-600 for errors), a
 * title, one line in text-2, one action. Retry is always "ניסיון חוזר".
 * Errors never look like data: a failed list is this block, never "0" or
 * "לא נמצא". Panel and inline errors are `role="status"` (no flood of
 * alerts); a page error announces once (`role="alert"`).
 *
 * `EmptyState`, `ErrorState`, `PanelError` and `PanelNote` are thin wrappers.
 */
export default function StateBlock({
  kind: kindIn,
  size = 'panel',
  title,
  description,
  icon,
  action,
  onRetry,
  retrying = false,
  error,
  compact = false,
  rows = 3,
  className = '',
}: StateBlockProps) {
  const { t } = useTranslation();
  let kind = normalizeKind(kindIn);
  if (kind === 'error' && error !== undefined && error !== null) kind = stateKindForError(error);

  if (kind === 'loading') {
    return <LoadingBlock size={size} rows={rows} label={typeof title === 'string' ? title : t('states.loading')} className={className} />;
  }

  const isError = kind === 'error';
  // `undefined` takes the kind's default; `null` hides the line.
  const mainLine = title !== undefined && title !== null ? title : kind === 'empty' ? t('states.empty.title') : t(`states.${kind}.title`);
  const subLine = description !== undefined ? description : kind === 'empty' ? null : t(`states.${kind}.text`);
  const Glyph = icon === null ? null : (icon ?? DEFAULT_ICON[kind]);
  const detail = isError ? errorDetail(error) : null;
  const retry = isError && onRetry ? (
    <RetryButton onRetry={onRetry} retrying={retrying} quiet={size === 'inline'} />
  ) : null;
  const role = isError ? (size === 'page' ? 'alert' : 'status') : undefined;

  if (size === 'inline') {
    return (
      <div role={role} className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-sm ${className}`}>
        {Glyph && <Icon icon={Glyph} size="md" className={`flex-shrink-0 ${isError ? 'text-danger-600' : 'text-text-3'}`} />}
        <span className="font-medium text-text">{mainLine}</span>
        {subLine && <span className="text-text-2">{subLine}</span>}
        {(retry || action) && (
          <span className="ms-auto flex items-center gap-2">
            {retry}
            {action}
          </span>
        )}
      </div>
    );
  }

  const small = compact;
  const pad = size === 'page' ? 'py-16' : small ? 'py-6' : 'py-12';
  return (
    <div role={role} className={`flex flex-col items-center justify-center px-5 text-center ${pad} ${className}`}>
      {Glyph && (
        <Icon
          icon={Glyph}
          size={small ? 'xl' : 'state'}
          className={`${small ? 'mb-2' : 'mb-3'} ${isError ? 'text-danger-600' : 'text-text-disabled'}`}
        />
      )}
      <p className={small ? 'text-sm font-medium text-text' : 'text-base font-semibold text-text'}>{mainLine}</p>
      {subLine && <p className={`text-sm text-text-2 max-w-sm ${small ? 'mt-0.5' : 'mt-1'}`}>{subLine}</p>}
      {(retry || action) && (
        <div className={`flex flex-wrap items-center justify-center gap-2 ${small ? 'mt-3' : 'mt-4'}`}>
          {retry}
          {action}
        </div>
      )}
      {detail && !small && (
        <details className="mt-3 max-w-sm text-xs text-text-3">
          <summary className="cursor-pointer rounded-xs hover:text-text-2">{t('states.details')}</summary>
          <p className="mt-1 break-words"><bdi dir="auto">{detail}</bdi></p>
        </details>
      )}
    </div>
  );
}

function RetryButton({ onRetry, retrying, quiet }: { onRetry: () => void; retrying: boolean; quiet: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => { if (!retrying) onRetry(); }}
      aria-disabled={retrying || undefined}
      className={quiet ? 'btn btn-ghost btn-sm text-accent-text' : 'btn btn-secondary btn-sm'}
    >
      {retrying
        ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        : <RotateCw size={14} aria-hidden="true" />}
      {t('states.retry')}
    </button>
  );
}

/** Content-shaped skeleton (no spinner): rows of two lines, like the Home lists. */
function LoadingBlock({ size, rows, label, className }: { size: StateBlockSize; rows: number; label: string; className: string }) {
  if (size === 'inline') {
    return (
      <div role="status" aria-busy="true" className={`flex items-center ${className}`}>
        <span className="sr-only">{label}</span>
        <span className="block h-3.5 w-40 rounded-xs bg-surface-subtle animate-pulse" aria-hidden="true" />
      </div>
    );
  }
  return (
    <div role="status" aria-busy="true" className={`${size === 'page' ? 'py-4' : 'py-2'} ${className}`}>
      <span className="sr-only">{label}</span>
      {Array.from({ length: Math.max(1, rows) }).map((_, i) => (
        <div key={i} className="px-5 py-3 animate-pulse" aria-hidden="true">
          <div className={`h-3.5 rounded-xs bg-surface-subtle ${i % 2 ? 'w-1/2' : 'w-2/3'}`} />
          <div className="mt-2 h-3 w-1/3 rounded-xs bg-surface-subtle" />
        </div>
      ))}
    </div>
  );
}

/**
 * The value of a number that failed to load: a quiet "—", never 0
 * (DESIGN_SYSTEM.md §14). Pair it with the visible hint `states.notLoaded`
 * ("לא נטען"), e.g. `<Kpi value={<NotLoadedValue />} hint={t('states.notLoaded')} />`.
 */
export function NotLoadedValue() {
  return <span className="text-text-3" aria-hidden="true">—</span>;
}
